import { useMemo } from "react";

// Common global dial codes for the back-of-pass phone field. Ordered by
// BackPocket's core AU/NZ market first, then top-10 by volume globally.
const COUNTRIES = [
  { code: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "+64", flag: "🇳🇿", name: "New Zealand" },
  { code: "+1",  flag: "🇺🇸", name: "US / Canada" },
  { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+353", flag: "🇮🇪", name: "Ireland" },
  { code: "+33", flag: "🇫🇷", name: "France" },
  { code: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "+34", flag: "🇪🇸", name: "Spain" },
  { code: "+39", flag: "🇮🇹", name: "Italy" },
  { code: "+31", flag: "🇳🇱", name: "Netherlands" },
  { code: "+41", flag: "🇨🇭", name: "Switzerland" },
  { code: "+45", flag: "🇩🇰", name: "Denmark" },
  { code: "+46", flag: "🇸🇪", name: "Sweden" },
  { code: "+47", flag: "🇳🇴", name: "Norway" },
  { code: "+65", flag: "🇸🇬", name: "Singapore" },
  { code: "+852", flag: "🇭🇰", name: "Hong Kong" },
  { code: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "+82", flag: "🇰🇷", name: "South Korea" },
  { code: "+86", flag: "🇨🇳", name: "China" },
  { code: "+91", flag: "🇮🇳", name: "India" },
  { code: "+971", flag: "🇦🇪", name: "UAE" },
  { code: "+27", flag: "🇿🇦", name: "South Africa" },
  { code: "+55", flag: "🇧🇷", name: "Brazil" },
  { code: "+52", flag: "🇲🇽", name: "Mexico" },
];

/**
 * PhoneWithCountry — a two-part phone input: a `<select>` for the dial code
 * and a plain `<input>` for the local number. The full E.164-ish string
 * ("+61 2 9000 0000") lives in the parent state via `onChange(value)`, so
 * downstream code (Apple Wallet, SMS, etc.) always gets a well-formed number.
 */
export function PhoneWithCountry({ value, onChange, placeholder = "2 9000 0000", testId }) {
  // Split existing "+61 2 9000 0000" → { code: "+61", rest: "2 9000 0000" }.
  const { code, rest } = useMemo(() => {
    const v = (value || "").trim();
    if (!v) return { code: "+61", rest: "" };
    const match = v.match(/^(\+\d{1,4})\s?(.*)$/);
    if (match) return { code: match[1], rest: (match[2] || "").trim() };
    return { code: "+61", rest: v };
  }, [value]);

  const write = (nextCode, nextRest) => {
    const trimmed = (nextRest || "").trim();
    onChange(trimmed ? `${nextCode} ${trimmed}` : "");
  };

  return (
    <div className="mt-1.5 flex rounded-xl overflow-hidden border border-[#E8E5DE] focus-within:border-[#0B0D10] transition-colors">
      <select
        data-testid={testId ? `${testId}-cc` : "phone-country-code"}
        value={code}
        onChange={(e) => write(e.target.value, rest)}
        className="bg-white h-11 pl-3 pr-2 text-sm border-r border-[#E8E5DE] outline-none appearance-none font-mono"
        aria-label="Country dialling code"
      >
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="tel"
        data-testid={testId ? `${testId}-number` : "phone-number"}
        value={rest}
        onChange={(e) => write(code, e.target.value)}
        placeholder={placeholder}
        className="bg-white h-11 px-3 text-sm flex-1 outline-none"
      />
    </div>
  );
}
